import { useNavigate } from "react-router-dom";

import {
  mapInvoiceIntegrationModulesToRows,
  useTenantIntegrationModulesForInvoices,
} from "../hooks/useTenantIntegrationModulesForInvoices";

import { DataTable } from "@/components/ui/v2/table/data-table";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";
import type { TenantIntegrationModuleForInvoice } from "@/features/invoices/hooks/useTenantIntegrationModulesForInvoices";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { useTenantIntegrationModuleForInvoiceTableColumns } from "@/lib/tableColumns/useTenantIntegrationModuleForInvoiceTableColumns";
import { getPreviousYearMonth } from "@/lib/utils/dateUtils";

const InvoiceIntegrationModulesPage = () => {
  const { tenantId } = useAuth();
  const { orderBy, descending } = useSortSearchParams();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const navigate = useNavigate();
  const [year, month] = getPreviousYearMonth();
  const handleSort = useTableSortHandler<TenantIntegrationModuleForInvoice>();

  const { data: paged, isLoading, isFetching } =
    useTenantIntegrationModulesForInvoices({
      tenantId,
      page,
      pageSize,
      search: debouncedSearchValue,
    });

  const tenantIntegrationModules = mapInvoiceIntegrationModulesToRows(
    paged?.data
  );

  const { tenantIntegrationModuleForInvoiceTableColumns } =
    useTenantIntegrationModuleForInvoiceTableColumns();

  const handleRowClick = (row: TenantIntegrationModuleForInvoice) => {
    navigate(
      `/${ROUTES.invoices}/${ROUTES.integrations}/${row.tenantIntegrationModuleId}/${ROUTES.toDo}/${year}/${month}`,
      { state: { integrationModuleName: row.name } }
    );
  };

  return (
    <div className="flex flex-col gap-8">
      <TableToolbar onSearchChange={setSearchValue} searchValue={searchValue} />

      <DataTable
        columns={tenantIntegrationModuleForInvoiceTableColumns}
        data={tenantIntegrationModules}
        isLoading={isLoading || isFetching}
        onRowClick={handleRowClick}
        orderBy={orderBy}
        descending={descending}
        onSort={handleSort}
        page={page}
        pageSize={pageSize}
        totalRecords={paged?.totalRecords ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowId={(row) => row.tenantIntegrationModuleId}
      />
    </div>
  );
};

export default InvoiceIntegrationModulesPage;
